import styled from '@emotion/styled';
import { Button, Form, message } from 'antd';
import JoditEditor, { type Jodit } from 'jodit-pro-react';
import { useMemo, useState } from 'react';
import tw from 'twin.macro';

import Modal from '@/components/Modal';
import { useUploadImage } from '@/hooks/mutations/admin';
import type { _JoditConfig } from '@/types/external';
import {
  DEFAULT_CONFIG,
  MAX_UPLOAD_IMAGE_COUNT,
  MAX_UPLOAD_IMAGE_SIZE,
  UPLOAD_ERROR_TYPE,
} from '@/utils/uploader';

interface JoditEditorProps extends _JoditConfig {
  customConfig?: _JoditConfig;
  onChange?: (value: string) => void;
  name?: string;
  required?: boolean;
  initialValue?: string;
  value?: never;
}

interface ErrorParams {
  files: File[];
  errMsg: string;
}

function defaultHandlerSuccess(that: typeof Jodit, imageUrls: string[]) {
  const jodit = that?.selection || that?.jodit?.selection;

  if (!jodit) return;

  for (const url of imageUrls) {
    jodit.insertImage(url, undefined, '100%');
  }
}

const Editor = ({
  customConfig,
  onChange,
  name,
  required,
  initialValue = '',
  ...rest
}: JoditEditorProps) => {
  const formInstance = Form.useFormInstance();
  // 이 컴포넌트가 antd Form 컴포넌트 내에 위치하면서 name을 할당하는 경우 antd useForm을 통해 관리되도록
  const isAntdForm = name && !!formInstance;
  const uploadImage = useUploadImage();
  const value = useMemo(() => {
    return initialValue;
  }, []);
  const [previewValue, setPreviewValue] = useState(initialValue);
  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);

  const uploader = {
    ...DEFAULT_CONFIG.uploader,
    url: '*',
    buildData: async (formData: FormData): Promise<ErrorParams> => {
      const files: File[] = [];

      // 파일 배열 가공
      Array.from(formData.entries()).forEach(([key, value]) => {
        if (key.startsWith('files[') && value instanceof File) {
          files.push(value);
        }
      });

      // 유효성 검사
      if (files.length > MAX_UPLOAD_IMAGE_COUNT) {
        return Promise.reject({
          files,
          errMsg: UPLOAD_ERROR_TYPE.MAX_UPLOAD_COUNT,
        });
      }

      const isValid = files.every((file) => {
        if (file.size > MAX_UPLOAD_IMAGE_SIZE) {
          return false;
        }

        return true;
      });

      return Promise.reject({
        files,
        errMsg: !isValid ? UPLOAD_ERROR_TYPE.MAX_SIZE : '',
      });
    },

    // eslint-disable-next-line object-shorthand
    error: async function ({ files, errMsg }: ErrorParams) {
      if (errMsg) {
        message.error(errMsg);
        return;
      }

      uploadImage.mutate({
        files,
        callback: defaultHandlerSuccess,
        that: this,
      });
    },
    defaultHandlerSuccess,
    getMessage: () => {},
  };

  // onChange, rest 새로 할당될 때마다 에디터에서 포커스가 풀려서 메모처리
  // 따라서 props에 따라 에디터 설정은 최초 1회밖에 안됨
  const editor = useMemo(() => {
    return (
      <EditorWrapper>
        <JoditEditor
          config={{
            ...DEFAULT_CONFIG,
            ...customConfig,
            uploader: {
              ...uploader,
            },
          }}
          value={value}
          onChange={(newValue) => {
            setPreviewValue(newValue);

            if (isAntdForm) {
              formInstance.setFieldValue(name, newValue);
            } else {
              onChange?.(newValue);
            }
          }}
          {...rest}
        />
      </EditorWrapper>
    );
  }, []);

  const PreviewButton = useMemo(
    () => <Button onClick={() => setIsOpenPreviewModal(true)}>미리보기</Button>,
    []
  );

  return (
    <>
      {isAntdForm ? (
        <div css={tw`flex flex-col items-end gap-[10px] w-full`}>
          {PreviewButton}
          <Form.Item
            name={name}
            rules={
              required
                ? [
                    {
                      required: true,
                      message: '상세 내용을 입력해주세요',
                      transform: (value) => {
                        return value.replace(/<\/?(p|br)>/g, '').trim();
                      },
                    },
                  ]
                : undefined
            }
            valuePropName="_" // Form.Item을 사용하는 경우 children에 value를 주입하는 것으로 추측되므로 에디터에 value 주입하지 않도록 처리
          >
            {editor}
          </Form.Item>
        </div>
      ) : (
        <div>
          {PreviewButton}
          {editor}
        </div>
      )}
      <Modal
        open={isOpenPreviewModal}
        footer={null}
        width="70%"
        styles={{
          body: {
            padding: '25px 15px',
            minWidth: '800px',
            minHeight: '600px',
            overflow: 'scroll',
          },
        }}
        onCancel={() => setIsOpenPreviewModal(false)}
      >
        <div
          className="rich-content"
          dangerouslySetInnerHTML={{
            __html: previewValue,
          }}
        />
      </Modal>
    </>
  );
};

export default Editor;

const EditorWrapper = styled.div`
  ${tw`[.jodit-wysiwyg]:min-h-full!`}
`;
